import { useState } from 'react';
import WrappedSlider from './Slider.js';
import { SliderArrowStyle } from './SliderComponents.js';

/**
 * SliderContainer component.
 *
 * @param {Object} props - The props for the SliderContainer component.
 * @param {React.ReactNode} props.children - The slides to render within the container.
 * @param {Function} [props.onNext=null] - Optional callback function to call when navigating to the next slide (optional).
 * @param {Function} [props.onPrevious=null] - Optional callback function to call when navigating to the previous slide (optional).
 * @param {number} [props.currentSlide=null] - The index of the current slide. If not provided, the component will default to the first slide (optional).
 * @param {boolean} [props.displayArrows=true] - Whether to display navigation arrows for the slider (optional, defaults to `true`).
 * @param {string} [props.arrowStyle] - Custom style for the navigation arrows (optional).
 * @param {string} [props.itemSpacing] - The amount of space between the items in the slider (optional).
 * @param {string[]} [props.arrowsTopPosition] - The top position for the navigation arrows (optional).
 *
 * @returns {JSX.Element} The SliderContainer component JSX.
 */
const SliderContainer = ({
	children,
	onNext = null,
	onPrevious = null,
	currentSlide = null,
	displayArrows = true,
	arrowStyle,
	itemSpacing,
	arrowsTopPosition
}) => {
	const hasCurrentSlide = currentSlide || currentSlide === 0;

	const [slideIndex, setSlideIndex] = useState(hasCurrentSlide ? currentSlide : 0);

	const setNextPage = () => {
		if (onNext) {
			onNext();
		} else {
			setSlideIndex(slideIndex < children.length - 1 ? slideIndex + 1 : slideIndex);
		}
	};

	const setPreviousPage = () => {
		if (onPrevious) {
			onPrevious();
		} else {
			setSlideIndex(slideIndex > 0 ? slideIndex - 1 : slideIndex);
		}
	};

	return (
		<WrappedSlider
			onNext={setNextPage}
			onPrevious={setPreviousPage}
			currentSlide={hasCurrentSlide ? currentSlide : slideIndex}
			displayArrows={displayArrows}
			arrowStyle={arrowStyle || SliderArrowStyle.embedded}
			arrowsTopPosition={arrowsTopPosition}
			itemSpacing={itemSpacing}
		>
			{children}
		</WrappedSlider>
	);
};

export default SliderContainer;
